import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../services/apiClientService';
import { extractTenantId } from '../configs/tenantUtils';

const USER_FACING_ERROR = "A szerveren jelenleg frissítés zajlik, kérlek, próbáld meg később.";

// A stable empty array: `?? []` would hand out a new one on every render, and the dashboard's
// grid window — which the boards' scroll effects depend on — is rebuilt whenever it changes.
const NO_DAILY_BREAKS = [];
const NO_OPENING_HOURS = [];
const NO_PROVIDERS = [];

// Refetched after the admin changes a coupon: hasActiveCoupon is part of the tenant.
export const TENANT_QUERY_KEY = ['tenantData'];

// How long an open tab trusts the tenant it loaded. Past it the tenant is fetched again — on a
// timer while the tab is shown, and when it is shown again — so a change made in the database (a
// new provider, other opening hours, a coupon expiring at midnight) reaches a page left open all
// day without a reload. The refetch swaps the data in place: nothing remounts or scrolls.
const TENANT_MAX_AGE_MS = 5 * 60_000;

const TenantContext = createContext();
export const useTenant = () => useContext(TenantContext);

const fetchTenant = async (tenantId) => {
  if (!tenantId) return;
  try {
    const response = await apiClient.get("/v1/public/tenants/" + tenantId);
    return response.data;
  } catch (error) {
    if (error?.code === "ERR_NETWORK") {
      throw new Error("A szerver nem érhető el.");
    }
    throw new Error(error?.response?.data?.message || "Hiba történt a lekérés során.");
  }
};

export const TenantProvider = ({ children }) => {
  const tenantId = extractTenantId();

  if (!tenantId) {
    console.error("[TenantProvider] Could not extract tenantId from hostname:", window.location.hostname);
  }

  const { data: tenant, isLoading, error } = useQuery({
    queryKey: TENANT_QUERY_KEY,
    queryFn: () => fetchTenant(tenantId),
    enabled: !!tenantId,
    staleTime: TENANT_MAX_AGE_MS,
    refetchInterval: TENANT_MAX_AGE_MS,
    refetchOnWindowFocus: true,
    retry: false,
    refetchOnMount: false,
  });

  if (error) {
    console.error("[TenantProvider] Failed to fetch tenant data:", error.message);
  }

  if (!isLoading && !error && !tenant) {
    console.error("[TenantProvider] Tenant data is empty for tenantId:", tenantId);
  }

  // A failed refetch keeps the tenant it already had (TanStack sets `error` beside the old
  // `data`), and the page stays up on it: RootLayout replaces the whole page with the error, so
  // only a tenant that never loaded counts — not a backend restarting while the tab was open.
  const hasError = !tenantId || (!isLoading && !tenant);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        tenantId,
        displayConfig: tenant?.displayConfig,
        homePage: tenant?.displayConfig?.homePage,
        legal: tenant?.displayConfig?.legal,
        isResourceProvider: tenant?.isResourceProvider ?? false,
        // Missing only on a backend older than the field: approval was then the one behaviour,
        // so that is the safe reading.
        isBookingApprovalRequired: tenant?.isBookingApprovalRequired ?? true,
        // Hours before the start within which a guest can no longer cancel a BOOKED booking.
        // Null, or missing on an older backend: no deadline (DECISIONS.md D28).
        cancellationDeadlineHours: tenant?.cancellationDeadlineHours ?? null,
        // Minutes before the start within which a guest can no longer book a slot; the backend
        // already leaves those slots out. Null, or missing on an older backend: no deadline
        // (DECISIONS.md D30).
        bookingDeadlineMinutes: tenant?.bookingDeadlineMinutes ?? null,
        // The periods inside opening hours the tenant takes no booking in; the backend already
        // leaves those slots out and the dashboard shades them. Empty, or missing on an older
        // backend: the tenant has none (DECISIONS.md D32).
        dailyBreaks: tenant?.dailyBreaks ?? NO_DAILY_BREAKS,
        // The tenant's whole week: one entry per open weekday with that day's hours, and no entry
        // for a day it is closed (DECISIONS.md D33). This is the only place hours come from.
        openingHours: tenant?.openingHours ?? NO_OPENING_HOURS,
        // The slot grid in minutes; the admin's time pickers step by it. Missing on an older
        // backend: the pickers then do not restrict the minutes.
        timeSlotDuration: tenant?.timeSlotDuration ?? null,
        resourceTypes: tenant?.resourceTypes ?? [],
        // Who serves the guests, in the tenant's order; every tenant has at least one. Missing on a
        // backend older than providers: none, which reads as one (docs/MULTI_PROVIDER.md).
        providers: tenant?.providers ?? NO_PROVIDERS,
        // With several, the guest chooses one, or „Bárki”, before the services.
        hasSeveralProviders: (tenant?.providers?.length ?? 0) > 1,
        // The admin's coupon page; missing on an older backend: no coupons.
        isCouponEnabled: tenant?.isCouponEnabled ?? false,
        // Whether a coupon can be redeemed today: the confirmation page shows its code field only
        // then. Refetched after the admin saves or closes a coupon (TENANT_QUERY_KEY).
        hasActiveCoupon: tenant?.hasActiveCoupon ?? false,
        loading: isLoading,
        error: hasError ? USER_FACING_ERROR : null,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;